<template>
    <div class="related-links">
        <div v-if="widget.Settings.ShowFilter" class="related-links__filter">
            <input v-model="filter" type="search" class="form-control" placeholder="Filter links">
        </div>

        <div v-if="
                isCollapsible &&
                widget.Settings.ShowCollapseAll &&
                filteredGroups.length && 
                !filter.trim()
            "
             class="related-links__collapse-controls">
            <button type="button" class="btn btn-primary" @click="toggleAll">
                {{ collapseButtonText }}
            </button>
        </div>

        <div v-if="!filteredGroups.length" class="related-links__empty">
            {{
                filter
                    ? 'No matching links.'
                    : widget.Settings.NoLinksText
            }}
        </div>

        <draggable :list="filteredGroups"
                   item-key="Id"
                   tag="div"
                   class="related-links__groups"
                   :disabled="!draggableGroups || !!filter"
                   handle=".related-links-admin__group-drag-handle"
                   ghost-class="related-links-admin__drag-ghost"
                   chosen-class="related-links-admin__drag-chosen"
                   drag-class="related-links-admin__dragging"
                   @start="$emit('group-drag-start', $event)"
                   @end="$emit('group-drag-end', $event)">

            <template #item="{ element: group }">
                <RelatedLinkGroup :group="group"
                                  :collapsible="isCollapsible"
                                  :expanded="isExpanded(group.Id)"
                                  :draggable-links="draggableLinks && !filter"
                                  :drag-group-name="`related-links-${widget.Id}`"
                                  @toggle="toggleGroup(group.Id)"
                                  @link-drag-start="linkDragStart"
                                  @link-drag-end="linkDragEnd"
                                  @link-added="linkAdded">

                    <template #title="{ group }">
                        <slot name="group-title" :group="group">
                            <h3 v-if="group.Name">
                                {{ group.Name }}
                            </h3>
                        </slot>
                    </template>

                    <template #actions="{ group }">
                        <slot name="group-actions" :group="group" />
                    </template>

                    <template #link="{ group, link }">
                        <slot name="link" :group="group" :link="link">
                            <RelatedLinkItem :link="link" />
                        </slot>
                    </template>

                    <template #before-links="{ group }">
                        <slot name="before-links" :group="group" />
                    </template>

                    <template #after-links="{ group }">
                        <slot name="after-links" :group="group" />
                    </template>

                </RelatedLinkGroup>
            </template>
        </draggable>
    </div>
</template>

<script setup>
    import {
        computed,
        ref,
        watch
    } from 'vue';

    import draggable from 'vuedraggable';
    import RelatedLinkGroup from './RelatedLinkGroup.vue';
    import RelatedLinkItem from './RelatedLinkItem.vue';
    import { matchesFilter, isLinkPublished } from './helpers';

    const props = defineProps({
        widget: {
            type: Object,
            required: true
        },

        draggableGroups: {
            type: Boolean,
            default: false
        },

        draggableLinks: {
            type: Boolean,
            default: false
        },

        showUnpublished: {
            type: Boolean,
            default: false
        }
    });

    const filter = ref('');
    const expandedGroupIds = ref([]);

    const visibleGroups = computed(() => {

        if (props.showUnpublished) {
            return props.widget.Groups;
        }

        const now = new Date();

        return props.widget.Groups
            .map(group => ({
                ...group,
                Links: group.Links.filter(link => isLinkPublished(link, now))
            }))
            .filter(group => group.Links.length);
    });
    
    const filteredGroups = computed(() => {
        const value = filter.value.trim();
        const groups = visibleGroups.value;

        if (!value) {
            return groups;
        }

        return groups
            .map(group => {
                if (matchesFilter(group.Name, value)) {
                    return group;
                }

                return {
                    ...group,
                    Links: group.Links.filter(link => {
                        return (
                            matchesFilter(link.Text, value) ||
                            matchesFilter(link.Description, value) ||
                            matchesFilter(link.Url, value)
                        );
                    })
                };
            })
            .filter(group => group.Links.length);
    });

    watch(
        () => [
            props.widget.Settings.IsCollapsible,
            props.widget.Settings.IsAccordion,
            props.widget.Settings.ExpandFirstGroup
        ],
        () => {
            setInitialExpansion();
        },
        {
            immediate: true
        }
    );

    watch(
        () => props.widget.Groups.map(x => x.Id),
        groupIds => {
            if (!props.widget.Settings.IsCollapsible) {
                expandedGroupIds.value = [...groupIds];
                return;
            }

            expandedGroupIds.value = expandedGroupIds.value
                .filter(id => groupIds.includes(id));

            if (
                props.widget.Settings.IsAccordion &&
                expandedGroupIds.value.length > 1
            ) {
                expandedGroupIds.value = [
                    expandedGroupIds.value[0]
                ];
            }
        }
    );

    function linkAdded(group, link, newIndex) {
        
        emit('link-added', group, link, newIndex);
    }
    
    function setInitialExpansion() {
        const groupIds = props.widget.Groups.map(x => x.Id);

        if (!props.widget.Settings.IsCollapsible) {
            expandedGroupIds.value = [...groupIds];
            return;
        }

        if (props.widget.Settings.ExpandFirstGroup && groupIds.length) {
            expandedGroupIds.value = [groupIds[0]];
            return;
        }

        expandedGroupIds.value = [];
    }

    function linkDragStart(group, event) {
        emit('link-drag-start', group, event);
    }

    function linkDragEnd(group, event) {
        emit('link-drag-end', group, event);
    }

    function isExpanded(groupId) {

        if (filter.value.trim()) {
            return true;
        }

        return expandedGroupIds.value.includes(groupId);
    }

    function toggleGroup(groupId) {

        if (!props.widget.Settings.IsCollapsible || filter.value.trim()) {
            return;
        }

        if (!props.widget.Settings.IsCollapsible) {
            return;
        }

        if (isExpanded(groupId)) {
            expandedGroupIds.value = expandedGroupIds.value.filter(id => id !== groupId);
            return;
        }

        if (props.widget.Settings.IsAccordion) {
            expandedGroupIds.value = [groupId];
            return;
        }

        expandedGroupIds.value = [
            ...expandedGroupIds.value,
            groupId
        ];
    }

    const emit = defineEmits([
        'group-drag-start',
        'group-drag-end',
        'link-drag-start',
        'link-drag-end',
        'link-added'
    ]);

    const isCollapsible = computed(() => {
        return props.widget.Settings.IsCollapsible ||
            props.widget.Settings.IsAccordion;
    });

    const anyExpanded = computed(() => {
        return filteredGroups.value.some(group =>
            isExpanded(group.Id)
        );
    });

    const allExpanded = computed(() => {
        return (
            filteredGroups.value.length > 0 &&
            filteredGroups.value.every(group =>
                isExpanded(group.Id)
            )
        );
    });

    const collapseButtonText = computed(() => {
        if (props.widget.Settings.IsAccordion) {
            return anyExpanded.value
                ? 'Collapse all'
                : 'Expand first';
        }

        return allExpanded.value
            ? 'Collapse all'
            : 'Expand all';
    });

    function toggleAll() {
        if (props.widget.Settings.IsAccordion) {
            if (anyExpanded.value) {
                expandedGroupIds.value = [];
                return;
            }

            const firstGroup = filteredGroups.value[0];

            expandedGroupIds.value = firstGroup
                ? [firstGroup.Id]
                : [];

            return;
        }

        if (allExpanded.value) {
            expandedGroupIds.value = [];
            return;
        }

        expandedGroupIds.value =
            filteredGroups.value.map(x => x.Id);
    }
</script>